import { AxiosPromise } from 'axios';
import httpClient from 'api/httpClient';

export enum PipelineRunOutcome {
  COMMITTED = 'COMMITTED',
  ROLLED_BACK = 'ROLLED_BACK',
  POST_COMMIT_FAILURE = 'POST_COMMIT_FAILURE',
  INCOMPLETE = 'INCOMPLETE',
}

export enum PipelineEvent {
  UOW_ENTRY = 'UOW_ENTRY',
  UOW_EXIT = 'UOW_EXIT',
}

/**
 * Financial + status snapshot of an order at a pipeline transition point.
 *
 * Every field is nullable, and null means "not recorded" — never zero. DataDog drops null-valued
 * attributes at ingest, so a field the backend emitted as null is simply absent from the log and
 * comes back as null here. Rendering it as 0 or $0.00 would show a fabricated financial value.
 */
export interface OrderPipelineSnapshot {
  status: string | null;
  orderTotal: number | null;
  netOrderTotal: number | null;
  balanceDue: number | null;
  orderItemsTotal: number | null;
  promotionAmount: number | null;
  taxAmountInCents: number | null;
  convenienceFee: number | null;
  tipAmount: number | null;
  creditAmount: number | null;
  loyaltyAmount: number | null;
  roundUpAmount: number | null;
  promotionIds: number[] | null;
  lineItemCount: number | null;
}

export type SnapshotField = keyof OrderPipelineSnapshot;

export interface SnapshotChange {
  field: SnapshotField;
  from: OrderPipelineSnapshot[SnapshotField];
  to: OrderPipelineSnapshot[SnapshotField];
}

/**
 * One instrumented unit of work inside a run. `details` is deliberately an open bag: each UoW logs
 * its own keys (`adjustBalanceDue` logs before/after balances, `orderCalculations` logs `computed`),
 * so it must be rendered generically rather than per-UoW.
 */
export interface PipelineRunStep {
  uow: string;
  event: PipelineEvent;
  at: string;
  details: Record<string, unknown>;
}

export interface PipelineRun {
  pipeline: string | null;
  origin: string | null;
  outcome: PipelineRunOutcome;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  error: string | null;
  pre: OrderPipelineSnapshot | null;
  post: OrderPipelineSnapshot | null;
  changes: SnapshotChange[];
  steps: PipelineRunStep[];
}

export interface OrderPipelineAuditWindow {
  from: string;
  to: string;
  /** Drives the on-screen coverage note — never hardcode this in the UI. */
  retentionDays: number;
  /** True when the requested window was older than retention and was moved forward. */
  clamped: boolean;
}

export interface OrderPipelineAuditResponse {
  serviceOrderId: string;
  window: OrderPipelineAuditWindow;
  /** True when more events exist than were returned. */
  truncated: boolean;
  /**
   * `heuristic` means runs were reconstructed from event timestamps rather than a run id, so two
   * runs seconds apart could in principle be merged or split. Don't present this as a definitive
   * audit log.
   */
  grouping: 'heuristic' | 'exact';
  runs: PipelineRun[];
}

export const getOrderPipelineAudit = (
  serviceOrderId: string | number,
): AxiosPromise<OrderPipelineAuditResponse> => {
  return httpClient({
    method: 'GET',
    url: `/super-admin/service-orders/${serviceOrderId}/pipeline-audit`,
  });
};
